import { useState } from 'react'
import { ChevronRight, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { PackagingSupplierPackagedItem } from '@/shared/hooks/api/packagingSuppliers'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { LexAssignPackagingToPartSheet } from './LexAssignPackagingToPartDialog'
import { PackagingItemSkuCell } from './PackagingItemSkuCell'
import type { SupplierPackagingCatalogRow } from './mapSupplierPackagingCatalogRows'

type SupplierYourPackagingItemsTableProps = {
  packagingSupplierId: string
  items: SupplierPackagingCatalogRow[]
  packagedItems: PackagingSupplierPackagedItem[]
  emptyMessage: string
  canWriteOrgData: boolean
  hasSupplierAddress: boolean
  onDeleteItem: (itemId: string) => void
  isDeleting: boolean
}

function LinkedPartsCountCell({
  linkedParts,
  linkedPartSkusLabel,
}: {
  linkedParts: SupplierPackagingCatalogRow['linkedParts']
  linkedPartSkusLabel: string
}) {
  const count = linkedParts.length

  if (count === 0) {
    return <span>{count}</span>
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='cursor-default underline decoration-dotted underline-offset-2'
        >
          {count}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className='w-80 max-w-sm' align='start' side='top'>
        <p className='mb-2 text-xs font-medium text-muted-foreground'>
          {linkedPartSkusLabel}
        </p>
        <ul className='space-y-2'>
          {linkedParts.map((part) => (
            <li key={part.id} className='text-sm'>
              <span className='font-medium'>{part.sku}</span>
              <span className='text-muted-foreground'> — {part.name}</span>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}

export function SupplierYourPackagingItemsTable({
  packagingSupplierId,
  items,
  packagedItems,
  emptyMessage,
  canWriteOrgData,
  hasSupplierAddress,
  onDeleteItem,
  isDeleting,
}: SupplierYourPackagingItemsTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [assignDialog, setAssignDialog] = useState<{
    packagingItemId: string
    linkedParts: SupplierPackagingCatalogRow['linkedParts']
  } | null>(null)

  const showAssignDialog = assignDialog !== null
  const columnCount = 5

  return (
    <>
      <div className='overflow-x-auto rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('items.columnName')}</TableHead>
              <TableHead>{t('items.columnSku')}</TableHead>
              <TableHead>
                {t('details.yourPackagingItems.partsAssigned')}
              </TableHead>
              <TableHead>{t('items.columnStatus')}</TableHead>
              <TableHead className='text-end' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length > 0 ? (
              items.map((item) => {
                const isStandalone = item.packagesOwnProduct === false
                const showAssignToPart =
                  canWriteOrgData && !isStandalone && hasSupplierAddress

                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Link
                        to={`/packaging/suppliers/${packagingSupplierId}/items/${item.id}`}
                        className='font-medium text-foreground underline decoration-dotted underline-offset-2'
                      >
                        {item.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {item.sku ? (
                        <PackagingItemSkuCell
                          sku={item.sku}
                          skuInBomList={item.skuInBomList ?? false}
                        />
                      ) : (
                        t('common:notAvailable')
                      )}
                    </TableCell>
                    <TableCell>
                      <LinkedPartsCountCell
                        linkedParts={item.linkedParts}
                        linkedPartSkusLabel={t(
                          'details.yourPackagingItems.linkedPartSkus'
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={item.completedAt ? 'success' : 'secondary'}
                      >
                        {item.completedAt
                          ? t('items.statusCompleted')
                          : t('items.statusPending')}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-end'>
                      <div className='flex items-center justify-end gap-1'>
                        {showAssignToPart && (
                          <Button
                            type='button'
                            variant='outline'
                            size='xs'
                            onClick={() => {
                              setAssignDialog({
                                packagingItemId: item.id,
                                linkedParts: item.linkedParts,
                              })
                            }}
                          >
                            {t('details.yourPackagingItems.assignToPart')}
                          </Button>
                        )}
                        {canWriteOrgData && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            aria-label={t('common:actions.delete')}
                            onClick={() => {
                              onDeleteItem(item.id)
                            }}
                            disabled={isDeleting}
                          >
                            <Trash2 className='size-4' />
                          </Button>
                        )}
                        <Button variant='ghost' size='icon-sm' asChild>
                          <Link
                            to={`/packaging/suppliers/${packagingSupplierId}/items/${item.id}`}
                            aria-label={t('details.yourPackagingItems.viewItem', {
                              name: item.name,
                            })}
                          >
                            <ChevronRight className='size-4' />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columnCount} className='p-0'>
                  <Empty className='rounded-none border-0 py-10'>
                    <EmptyHeader>
                      <EmptyTitle>{emptyMessage}</EmptyTitle>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showAssignDialog && (
        <LexAssignPackagingToPartSheet
          open={showAssignDialog}
          onOpenChange={(open) => {
            if (!open) {
              setAssignDialog(null)
            }
          }}
          supplierId={packagingSupplierId}
          packagingItemId={assignDialog.packagingItemId}
          linkedParts={assignDialog.linkedParts}
          packagedItems={packagedItems}
        />
      )}
    </>
  )
}
